
import { FC } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Message } from "@/types/chat";
import { secureHtmlSanitize, secureDecodeHtmlEntities } from "@/utils/secureHtmlUtils";

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: FC<ChatMessageProps> = ({ message }) => {
  const formattedTime = message.timestamp.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  // Process text for markdown-like formatting 
  const formatText = (text: string) => {
    // Decode any HTML entities first
    const decodedText = secureDecodeHtmlEntities(text);
    
    // Replace **text** with bold
    const boldFormatted = decodedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Replace ### Heading with H3 tags
    const h3Formatted = boldFormatted.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2 text-foreground">$1</h3>');
    
    // Replace ## Heading with H2 tags
    const h2Formatted = h3Formatted.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2 text-foreground">$1</h2>');
    
    // Replace # Heading with H1 tags
    const h1Formatted = h2Formatted.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2 text-foreground">$1</h1>');
    
    // Replace *text* or _text_ with italic
    const italicFormatted = h1Formatted.replace(/(\*|_)([^*_]+)\1/g, '<em>$2</em>');
    
    // Step 1: Replace markdown links [text](url) with clickable links FIRST
    // This prevents conflicts with URL processing
    let processedText = italicFormatted;
    
    // Process markdown links with a more robust approach
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    const replacements = [];
    
    // Find all markdown links and store replacements
    while ((match = markdownLinkRegex.exec(processedText)) !== null) {
      const [fullMatch, text, url] = match;
      const cleanUrl = url.trim();
      const isInternalUrl = cleanUrl.includes('gildy.it') || cleanUrl.includes('www.gildy.it');
      
      let replacement;
      if (isInternalUrl) {
        replacement = `<a href="${cleanUrl}" class="text-metal-primary underline hover:text-metal-primary/80 transition-colors">${text}</a>`;
      } else {
        replacement = `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-metal-primary underline hover:text-metal-primary/80 transition-colors inline-flex items-center gap-1">${text}<span class="inline-block w-3 h-3"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></span></a>`;
      }
      
      replacements.push({
        start: match.index,
        end: match.index + fullMatch.length,
        replacement: replacement
      });
    }
    
    // Apply replacements from end to start to maintain indices
    for (let i = replacements.length - 1; i >= 0; i--) {
      const { start, end, replacement } = replacements[i];
      processedText = processedText.substring(0, start) + replacement + processedText.substring(end);
    }
    
    // Step 2: Replace remaining standalone URLs that are NOT already inside HTML tags
    // This catches URLs that weren't part of markdown links
    const linkFormatted = processedText.replace(
      /(https?:\/\/[^\s<>"']+)(?![^<]*<\/a>)(?![^<]*">)/g, 
      (match) => {
        // Clean the URL by removing any trailing punctuation that might interfere
        const cleanUrl = match.replace(/[").,;!?]+$/, '');
        const isInternalUrl = cleanUrl.includes('gildy.it') || cleanUrl.includes('www.gildy.it');
        
        if (isInternalUrl) {
          // Internal link without target blank or external icon
          return `<a href="${cleanUrl}" class="text-metal-primary underline hover:text-metal-primary/80 transition-colors">${cleanUrl}</a>`;
        } else {
          // External link with target blank and external icon
          return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-metal-primary underline hover:text-metal-primary/80 transition-colors inline-flex items-center gap-1">${cleanUrl}<span class="inline-block w-3 h-3"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></span></a>`;
        }
      }
    );
    
    // Replace simple lists (- item or * item)
    const listFormatted = linkFormatted.replace(/^[\-\*] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    
    // Replace inline code with backticks
    const codeFormatted = listFormatted.replace(/`([^`]+)`/g, '<code class="bg-secondary px-1 py-0.5 rounded text-sm font-mono">$1</code>');
    
    // Apply sanitization
    const sanitized = secureHtmlSanitize(codeFormatted, 'CHAT');
    
    // Split by line breaks and wrap in paragraphs
    return sanitized.split('\n').map((line, i) => 
      line ? <p key={i} dangerouslySetInnerHTML={{ __html: line }} /> : <br key={i} />
    );
  };

  return (
    <div className={cn(
      "flex items-start gap-3 group",
      message.isUser ? "flex-row-reverse" : ""
    )}>
      <Avatar className={cn(
        "w-8 h-8",
        message.isUser ? "bg-metal-primary text-white" : "bg-secondary text-foreground dark:text-secondary-foreground"
      )}>
        {!message.isUser && <AvatarImage src="/gildy-avatar.png" alt="Gildy" />}
        <AvatarFallback className={message.isUser ? "text-white" : "text-foreground dark:text-secondary-foreground"}>
          {message.isUser ? "U" : "G"}
        </AvatarFallback>
      </Avatar>
      
      <div className={cn(
        "rounded-lg p-3 max-w-[80%]",
        message.isUser 
          ? "bg-metal-primary text-black dark:text-white" 
          : "bg-secondary text-foreground dark:text-secondary-foreground"
      )}>
        <div className="whitespace-pre-wrap font-medium text-left">
          {formatText(message.text)}
        </div>
        <div className={cn(
          "text-xs mt-1 text-right",
          message.isUser 
            ? "text-black/80 dark:text-white/80" 
            : "text-foreground/70 dark:text-secondary-foreground/70"
        )}>
          {formattedTime}
        </div>
      </div>
    </div>
  );
}
